/**
 * Pure guards for Felix client-routed actions.
 *
 * These live outside FelixChat.tsx so they can be tested for BEHAVIOUR rather
 * than by grepping the component's source and re-implementing the logic in the
 * test — a pattern that passes even after the real code drifts away from it.
 *
 * Both guards exist because a tool schema and its API client can each be
 * permissive while their INTERSECTION is not safe:
 *
 *  - `lead_capture`: CaptureLeadInput and the tool schema both mark every field
 *    optional, so `{}` type-checks and POSTs cleanly — creating a lead nobody
 *    can contact. It reports success, so the failure is invisible until someone
 *    tries to follow up.
 *
 *  - `draft_social_post`: the tool enum and SocialPlatform agree on all four
 *    names today, but Claude emits free-form strings and the backend rejects a
 *    whole post over one unrecognised platform. Dropping the bad entry keeps a
 *    three-platform post from failing because of a fourth.
 */

import type { SocialPlatform } from "@/api/socialApi";

export const SOCIAL_PLATFORMS: readonly SocialPlatform[] = [
  "linkedin",
  "x",
  "facebook",
  "instagram",
];

export interface LeadContact {
  name: string;
  email: string;
}

/**
 * A lead is contactable if it has a name OR an email. Returns the trimmed pair,
 * or null when neither is present — the caller turns null into a question for
 * the user rather than a silent empty row.
 */
export function leadContact(params: Record<string, unknown>): LeadContact | null {
  const name = String(params.name ?? "").trim();
  const email = String(params.email ?? "").trim();
  if (!name && !email) return null;
  return { name, email };
}

/**
 * Normalise whatever the model produced into known platforms: lower-cased,
 * de-duplicated, unknown values dropped. An empty result means "ask which
 * platform" — never "post everywhere".
 */
export function normaliseSocialPlatforms(raw: unknown): SocialPlatform[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: SocialPlatform[] = [];
  for (const entry of raw) {
    const name = String(entry ?? "").trim().toLowerCase();
    if (seen.has(name)) continue;
    seen.add(name);
    if ((SOCIAL_PLATFORMS as readonly string[]).includes(name)) {
      out.push(name as SocialPlatform);
    }
  }
  return out;
}

const DEFECT_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** defects.title is varchar(500); DefectCreate enforces the same limit. */
export const DEFECT_TITLE_MAX = 500;

export interface DefectPayload {
  project_id: string;
  title: string;
  description: string;
  severity?: string;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  linked_user_story_id?: string;
  linked_impl_task_id?: string;
}

/** Text as the column stores it: a list of steps is one line per item. */
function defectText(raw: unknown): string | undefined {
  if (raw === null || raw === undefined) return undefined;
  const text = Array.isArray(raw)
    ? raw
        .filter((item) => item !== null && item !== undefined)
        .map((item) => (typeof item === "string" ? item : JSON.stringify(item)))
        .join("\n")
    : typeof raw === "string"
      ? raw
      : JSON.stringify(raw);
  return text.trim() ? text : undefined;
}

/**
 * The body for `defectsApi.create` from a `create_defect` card that the org's
 * autonomy level withheld for confirmation — or the sentence to show instead
 * of calling the API at all, so a blank field never becomes a raw 422.
 *
 * Severity is deliberately NOT mapped here. The server runs every defect
 * through app/workflows/severity.coerce_severity (MAJOR -> HIGH, MINOR -> LOW),
 * and a second copy of that vocabulary in the browser would drift — the
 * Defects page already maps its own "Minor" label to MEDIUM, the opposite of
 * the shared rule. It is sent as given, or omitted.
 *
 * Never sent: `priority` (no such column), `code`, `assigned_to`,
 * `test_case_id`, `test_execution_id` — the two server-side doors drop the
 * same four.
 */
export function defectPayload(
  params: Record<string, unknown>,
  projectId: string | undefined,
): { payload: DefectPayload } | { refusal: string } {
  if (!projectId) {
    return { refusal: "Open a project first — a defect is filed in a project." };
  }
  const title = defectText(params.title);
  const description = defectText(params.description);
  if (!title || !description) {
    return { refusal: "A defect needs a title and a description." };
  }
  if (title.length > DEFECT_TITLE_MAX) {
    return {
      refusal: `The defect title is longer than ${DEFECT_TITLE_MAX} characters.`,
    };
  }
  const payload: DefectPayload = { project_id: projectId, title, description };
  const severity =
    typeof params.severity === "string" ? params.severity.trim() : "";
  if (severity) payload.severity = severity;
  for (const key of [
    "steps_to_reproduce",
    "expected_behavior",
    "actual_behavior",
  ] as const) {
    const text = defectText(params[key]);
    if (text) payload[key] = text;
  }
  for (const key of ["linked_user_story_id", "linked_impl_task_id"] as const) {
    const id = params[key];
    if (typeof id === "string" && DEFECT_UUID_RE.test(id)) payload[key] = id;
  }
  return { payload };
}
